import { isPlatformServer, NgClass } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    PLATFORM_ID,
    signal,
    untracked,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { outputFromObservable, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SlideDirective, SliderComponent, type SliderOptions } from '@hyundai/ng-common-lib';
import { M060C010VehicleShowroomItemComponent } from '../m060-c010-vehicle-showroom-item/m060-c010-vehicle-showroom-item.component';
import type { M060C010VehicleShowroom } from '../m060-c010-vehicle-showroom.interface';

@Component({
    selector: 'ui-m060-c010-vehicle-showroom-slider',
    standalone: true,
    templateUrl: './m060-c010-vehicle-showroom-slider.component.html',
    styleUrls: ['./m060-c010-vehicle-showroom-slider.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [M060C010VehicleShowroomItemComponent, SliderComponent, SlideDirective, NgClass],
})
export class M060C010VehicleShowroomSliderComponent {
    activatedRoute = inject(ActivatedRoute);
    router = inject(Router);
    platformId = inject(PLATFORM_ID);

    activeParams = toSignal(this.activatedRoute.queryParams);

    isFirstView = input.required({ transform: booleanAttribute });
    cars = input.required<M060C010VehicleShowroom[]>();

    readonly sliderCmp = viewChild.required(SliderComponent);

    readonly currentSlide = signal(0);

    readonly currentIndex = computed(() => this.currentSlide());

    readonly emitCurrentTabIndex = outputFromObservable(toObservable(this.currentIndex));

    readonly sliderOptions: SliderOptions = {
        initial: 0,
        renderMode: 'performance',

        slides: {
            perView: 'auto',
            origin: 'center',
        },

        breakpoints: {
            '(max-width: 767px)': {
                slides: {
                    perView: 1.6,
                },
            },
        },

        slideChanged: (slider) => {
            this.currentSlide.set(slider.track.details.rel);
        },
    };

    readonly slideToIndexEffect = effect(() => {
        if (isPlatformServer(this.platformId)) {
            return;
        }

        const carParam = this.activeParams()?.['utm_creative_format'] as string | undefined;
        if (!carParam) {
            return;
        }

        const cars = this.cars();
        if (cars.length === 0) {
            return;
        }

        const keen = this.sliderCmp().slider();
        if (!keen) {
            return;
        }

        untracked(() => {
            const normalizedCarParam = this.normalizeCreativeValue(carParam);
            const initIndex = cars.findIndex((car) => {
                const deepLinkValue = this.normalizeCreativeValue(car?.deepLinkUtmCreativeValue);
                return deepLinkValue !== '' && deepLinkValue === normalizedCarParam;
            });

            if (initIndex !== -1) {
                this.slideToIndex(initIndex);
            }

            this.slideToIndexEffect.destroy();
        });
    });

    slideToIndex(index: number): void {
        const sliderInstance = this.sliderCmp().slider();
        if (!sliderInstance) {
            return;
        }

        sliderInstance.moveToIdx(index, true, { duration: 0 });
        requestAnimationFrame(() => {
            this.sliderCmp().slider()?.moveToIdx(index, true, { duration: 0 });
        });
    }

    private normalizeCreativeValue(value: string | null | undefined): string {
        const safeValue = value ?? '';
        let decodedValue = safeValue;
        try {
            decodedValue = decodeURIComponent(safeValue);
        } catch {
            decodedValue = safeValue;
        }

        return decodedValue
            .trim()
            .toLowerCase()
            .replaceAll(/[\s_-]+/g, '');
    }
}
